import React, { useContext, useEffect, useState } from 'react';
import { List, ListHeader, DataColumn, LinkColumn } from 'components/lists';
import { useAggregate } from 'hooks';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Config } from 'configs';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { enrichProductCategoriesResponse, extractRecordsFromAggreagte, sortByRootParent } from 'utils/renderingHelpers';
import { PageContent } from 'components/layouts/pageContent';
import { ListLayout } from 'components/layouts/listLayout';
import { getTranslatedData } from 'locales';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { FilterBlock, FilterField } from 'components/layouts/filterBlock';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Pagination } from 'components/lists/pagination';

export const ProductCategories = () => {
  const { state: auth } = useContext(AuthContext);
  const { state: companyState, getCompanyRestaurants, deleteById, shouldDisplayNoRestaurantBlock } = useContext(CompanyContext);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);

  const [filterRestaurant, setFilterRestaurant, filterRestaurantRef] = useLocalStorage(`product-categories-filter-restaurant-${auth.company?.company}`, null);
  const [filterHidden, setFilterHidden, filterHiddenRef] = useLocalStorage(`product-categories-filter-hidden-${auth.company?.company}`, 'all');

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.PRODUCT_CATEGORIES,
    aggregateId: Config.DATA.AGGREGATES.PRODUCT_CATEGORIES_LIST,
    secretKey: auth.token,
    filterBuilder: () => {
      const filter = { company: auth.company?.company };

      if (filterRestaurantRef.value) {
        filter.restaurantFilter = `{ restaurants: { $eq: ObjectId('${filterRestaurantRef.value}') } }`;
      } else {
        filter.restaurantFilter = {};
      }

      if (filterHiddenRef.value === 'hidden') {
        filter.hiddenFilter = '{ hidden: { $eq: true } }';
      } else if (filterHiddenRef.value === 'visible') {
        filter.hiddenFilter = '{ hidden: { $ne: true } }';
      } else {
        filter.hiddenFilter = {};
      }

      return filter;
    },
    pageSize: Config.DEFAULTS.PAGE_SIZE,
    pageNumber: currentPage - 1,
    loadOnInit: true,
    aggregateIdV2: 'AdminListProductCategories',
    companyIdForV2: auth.company?.company,
  };

  const [findProductCategories, productCategoriesResponse, isLoading] = useAggregate(requestData);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const onFilterCategories = () => {
    findProductCategories();
  };

  const onClearFilters = () => {
    setFilterRestaurant('');
    setFilterHidden('all');
    findProductCategories();
  };

  const onEditClicked = (item) => {
    navigate(`/settings/product-categories/${item._id}`);
  };

  const onDeleteClicked = async (item) => {
    await deleteById(
      {
        collectionName: Config.DATA.COLLECTIONS.PRODUCT_CATEGORIES,
        token: auth.token,
        id: item._id,
        resourceV2: 'AdminProductCategory',
        companyIdForV2: auth.company?.company,
      },
      async () => {
        await findProductCategories(true, requestData);
      },
    );
  };

  if (isLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD && shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  const { paginatedResults: productCategories, totalCount } = extractRecordsFromAggreagte(productCategoriesResponse);

  return (
    <ListLayout
      isLoading={isLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD || companyState.isLoadingRestaurants}
    >
      <PageTitle
        title="Product Categories"
        breadcrumbs={[{ path: '/settings', title: 'Settings' }]}
        isTopLevel
        hideMobile
      />
      <PageContent>
        <FilterBlock buttons={[
          {
            onPress: onClearFilters,
            text: 'Clear',
            class: 'bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 inline-flex justify-center items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
          },
          {
            onPress: onFilterCategories,
            text: 'Search',
            class: 'ml-3 bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center items-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
            isLoading: !!isLoading,
          },
        ]}
        >
          <FilterField>
            <DropDownList
              showLabel
              id="restaurants"
              name="restaurants"
              label="Restaurant"
              value={filterRestaurant}
              onChange={(e) => {
                setFilterRestaurant(e.target.value);
              }}
              options={[{ label: 'All', value: '' }, ...(companyState.restaurants || []).map((restaurant) => ({
                label: restaurant.name,
                value: restaurant._id,
              }))]}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
          <FilterField>
            <DropDownList
              showLabel
              id="hidden"
              name="hidden"
              label="Hidden"
              value={filterHidden}
              onChange={(e) => {
                setFilterHidden(e.target.value);
              }}
              options={Config.DATA.FILTER_HIDDEN_OPTIONS}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
        </FilterBlock>
        <ListHeader title="Product categories">
          <button
            type="button"
            onClick={() => navigate(`/settings/product-categories/add-new?restaurant=${filterRestaurant}`)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new
          </button>
        </ListHeader>
        <List
          source={enrichProductCategoriesResponse(productCategories)?.sort(sortByRootParent)}
          onRowClick={onEditClicked}
          editCollection={Config.DATA.COLLECTIONS.PRODUCT_CATEGORIES}
        >
          <DataColumn
            name="Name"
            dbColumnName="name"
            render={(productCategory) => {
              return getTranslatedData(productCategory.name);
            }}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Parents"
            render={(productCategory) => {
              const splitName = productCategory.displayName ? productCategory.displayName.split('->') : [];

              if (splitName && splitName.length > 0) {
                splitName.pop();
              }

              return <div>{splitName.map((x, ind) => <div>{x}{ind === splitName.length - 1 ? '' : ' ->'}</div>)}</div>;
            }}
            dbColumnName="displayName"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Group"
            render={(item) => {
              return item.group_category ? getTranslatedData(item.group_category.name) : '';
            }}
            dbColumnName="group_category"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Order"
            dbColumnName="order"
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
            editSettings={{
              type: 'integer',
              valueField: 'order',
              dbField: 'order',
            }}
          />
          <DataColumn
            name="Hidden"
            dbColumnName="hidden"
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
            editSettings={{
              type: 'boolean',
              valueField: 'hidden',
              dbField: 'hidden',
            }}
          />
          <LinkColumn
            title="Delete"
            onClick={onDeleteClicked}
            askForConfirmation
            className="relative py-3.5 pl-3 sm:pr-6"
            dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
          />
          <Pagination
            pageSize={Config.DEFAULTS.PAGE_SIZE}
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChanged={async (pageNumber) => {
              const request = { ...requestData, pageNumber: pageNumber - 1 };

              await findProductCategories(false, request);
              setCurrentPage(pageNumber);
            }}
          />
        </List>
      </PageContent>
    </ListLayout>
  );
};
